import React, { useRef, useState } from 'react';

import { getPresetSvg } from './presets.js';
import {
  LayersWrap,
  StickerBox,
  StickerContent,
  StickerHandle,
  StickerLayerMenu,
  StickerLayerMenuItem,
  StickerLink,
  StickerOutline,
  StickerRotateHandle,
  StickerToolbar,
  StickerToolButton,
} from './stickerLayer.styled.js';

const round1 = (n) => Math.round(n * 10) / 10;
const stop = (e) => e.stopPropagation();

const LinkIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5' />
    <path d='M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5' />
  </svg>
);
const LayersIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinejoin='round'>
    <path d='M12 2 2 7l10 5 10-5z' />
    <path d='m2 17 10 5 10-5' />
    <path d='m2 12 10 5 10-5' />
  </svg>
);
const TrashIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M3 6h18M8 6V4h8v2m-2 0v13H10V6m-4 0 1 14h10l1-14' />
  </svg>
);

// Renders the sticker graphic. emoji = text glyph, preset = our inline SVG,
// image = an uploaded CDN url. dangerouslySetInnerHTML is safe here: preset SVGs
// are static strings we author in presets.js, never user input.
const StickerInner = ({ sticker }) => {
  if (sticker.kind === 'emoji') return <span style={{ display: 'block', lineHeight: 1 }}>{sticker.value}</span>;
  if (sticker.kind === 'image') return <img src={sticker.value} alt='' draggable={false} style={{ display: 'block', width: '100%', height: 'auto' }} />;
  const svg = getPresetSvg(sticker.value);
  // aspect-ratio keeps the square preset SVG (height="100%") from collapsing inside an auto-height span.
  return svg ? <span style={{ display: 'block', width: '100%', aspectRatio: '1' }} dangerouslySetInnerHTML={{ __html: svg }} /> : null;
};

// One sticker. The transform handlers (move / resize / rotate), layer ordering and link
// editing only wire up when `editable` (Dashboard preview, sticker mode). On the live site
// `editable` is false: the sticker is inert unless it has a link, in which case it's a
// clickable anchor.
export const Sticker = ({ sticker, layerWidth, editable, selected, onSelect, onChange, onDelete, onLayer }) => {
  const { x, y, size, rotation, flipX, opacity, link, z } = sticker;
  const boxRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const pxSize = layerWidth ? (layerWidth * size) / 100 : 0;

  const drag = (onMove) => {
    const move = (ev) => onMove(ev);
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const startMove = (e) => {
    if (!editable) return;
    e.stopPropagation();
    onSelect?.();
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = x;
    const origY = y;
    // Measure the layer width live at drag start (offsetParent === StickerLayerRoot).
    // The `layerWidth` prop can be 0/stale on the first interaction, which would lock
    // horizontal movement (dxPct === 0) while vertical still worked.
    const lw = boxRef.current?.offsetParent?.getBoundingClientRect().width || layerWidth || 0;
    drag((ev) => {
      const dxPct = lw ? ((ev.clientX - startX) / lw) * 100 : 0;
      const nx = Math.min(100, Math.max(0, origX + dxPct));
      const ny = Math.max(0, origY + (ev.clientY - startY));
      onChange?.({ x: round1(nx), y: Math.round(ny) });
    });
  };

  const center = () => {
    const box = boxRef.current?.getBoundingClientRect();
    return box ? { cx: box.left + box.width / 2, cy: box.top + box.height / 2 } : null;
  };

  const startResize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    const c = center();
    if (!c) return;
    const startDist = Math.hypot(e.clientX - c.cx, e.clientY - c.cy);
    const origSize = size;
    drag((ev) => {
      const dist = Math.hypot(ev.clientX - c.cx, ev.clientY - c.cy);
      const ratio = startDist ? dist / startDist : 1;
      const ns = Math.min(90, Math.max(4, origSize * ratio));
      onChange?.({ size: round1(ns) });
    });
  };

  const startRotate = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    const c = center();
    if (!c) return;
    const angleAt = (ev) => (Math.atan2(ev.clientY - c.cy, ev.clientX - c.cx) * 180) / Math.PI;
    const startAngle = angleAt(e);
    const origRot = rotation;
    drag((ev) => {
      let nr = origRot + (angleAt(ev) - startAngle);
      nr = ((nr % 360) + 360) % 360;
      onChange?.({ rotation: Math.round(nr) });
    });
  };

  const handleLink = () => {
    const next = window.prompt('Link URL (leave empty to remove):', link || '');
    if (next === null) return;
    onChange?.({ link: next.trim() || null });
  };

  const layer = (direction) => {
    onLayer?.(direction);
    setMenuOpen(false);
  };

  const content = (
    <StickerContent style={{ fontSize: pxSize ? `${pxSize}px` : '40px', transform: flipX ? 'scaleX(-1)' : 'none' }}>
      <StickerInner sticker={sticker} />
    </StickerContent>
  );

  return (
    <StickerBox
      ref={boxRef}
      data-sticker-id={sticker.id}
      $editable={editable}
      $linked={!editable && !!link}
      onPointerDown={startMove}
      style={{
        left: `${x}%`,
        top: `${y}px`,
        width: pxSize ? `${pxSize}px` : `${size}%`,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        opacity,
        zIndex: z,
      }}
    >
      {!editable && link ? (
        <StickerLink href={link} target='_blank' rel='noopener noreferrer'>
          {content}
        </StickerLink>
      ) : (
        content
      )}

      {selected && (
        <>
          <StickerOutline />
          <StickerRotateHandle onPointerDown={startRotate} />
          <StickerHandle $pos='tl' onPointerDown={startResize} />
          <StickerHandle $pos='tr' onPointerDown={startResize} />
          <StickerHandle $pos='bl' onPointerDown={startResize} />
          <StickerHandle $pos='br' onPointerDown={startResize} />

          <StickerToolbar $rotation={rotation} onPointerDown={stop}>
            <StickerToolButton type='button' title={link ? 'Edit link' : 'Add link'} $active={!!link} onPointerDown={stop} onClick={handleLink}>
              <LinkIcon />
            </StickerToolButton>

            <LayersWrap>
              <StickerToolButton type='button' title='Layer' $active={menuOpen} onPointerDown={stop} onClick={() => setMenuOpen((o) => !o)}>
                <LayersIcon />
              </StickerToolButton>
              {menuOpen && (
                <StickerLayerMenu onPointerDown={stop}>
                  <StickerLayerMenuItem type='button' onClick={() => layer('front')}>
                    Bring to front
                  </StickerLayerMenuItem>
                  <StickerLayerMenuItem type='button' onClick={() => layer('forward')}>
                    Bring forward
                  </StickerLayerMenuItem>
                  <StickerLayerMenuItem type='button' onClick={() => layer('backward')}>
                    Send backward
                  </StickerLayerMenuItem>
                  <StickerLayerMenuItem type='button' onClick={() => layer('back')}>
                    Send to back
                  </StickerLayerMenuItem>
                </StickerLayerMenu>
              )}
            </LayersWrap>

            <StickerToolButton type='button' title='Delete' $danger onPointerDown={stop} onClick={() => onDelete?.()}>
              <TrashIcon />
            </StickerToolButton>
          </StickerToolbar>
        </>
      )}
    </StickerBox>
  );
};
